import React from 'react';
import { TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { format, parse } from 'date-fns';

interface DatePickerFieldProps {
  label: string;
  value: string; // Formato dd/mm/yyyy
  onChange: (value: string) => void;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
}

const DatePickerField: React.FC<DatePickerFieldProps> = ({
  label,
  value,
  onChange,
  error = false,
  helperText = '',
  required = false,
  disabled = false,
  fullWidth = true,
  size = 'medium',
}) => {
  // Converte string dd/mm/yyyy para Date
  const stringToDate = (dateString: string): Date | null => {
    if (!dateString) return null;
    
    // Verifica se está no formato dd/mm/yyyy
    const match = dateString.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!match) return null;
    
    const [, day, month, year] = match;
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    
    // Verifica se a data é válida
    if (isNaN(date.getTime())) return null;
    
    return date;
  };

  // Converte Date para string dd/mm/yyyy
  const dateToString = (date: Date | null): string => {
    if (!date) return '';
    return format(date, 'dd/MM/yyyy');
  };

  // Converte o valor string para Date para o DatePicker
  const dateValue = stringToDate(value);

  const handleDateChange = (newDate: Date | null) => {
    const dateString = dateToString(newDate);
    onChange(dateString);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      <DatePicker
        label={label}
        value={dateValue}
        onChange={handleDateChange}
        disabled={disabled}
        slotProps={{
          textField: {
            fullWidth,
            size,
            error,
            helperText,
            required,
          },
        }}
        format="dd/MM/yyyy"
        views={['day', 'month', 'year']}
      />
    </LocalizationProvider>
  );
};

export default DatePickerField;
